// @file: src/components/scoreboard/styles/ScoreBoard.styles.ts

import React from 'react';
import { Theme } from '@config/themes/ITheme';
import type { DeviceType } from '@platform/contracts';

/**
 * Creates a style object for the Scoreboard component using a given theme.
 * @param theme - The theme object to apply to the styles.
 * @param deviceType - The current device type (mobile, tablet, desktop).
 * @param isLandscape - Whether the device is in landscape mode.
 * @returns A record of CSS properties for styling the component.
 */
export const createScoreboardStyles = (
  theme: Theme,
  deviceType: DeviceType,
  _isLandscape: boolean
): Record<string, React.CSSProperties> => {
  // --- CONSTANTS ---
  const FOOTER_HEIGHT = 'clamp(30px, 4vh, 54px)';

  // --- Base style for filter buttons ---
  const filterButtonBase: React.CSSProperties = {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    padding: '0px 4px',
    backgroundColor: 'transparent',
    color: '#a3a3a3',
    border: 'none',
    borderRadius: '0',
    cursor: 'pointer',
    fontSize: '10px',
    fontWeight: '500',
    lineHeight: 1.15,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    textAlign: 'center',
    transition: 'color 0.1s',
    minHeight: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    pointerEvents: 'auto',
    boxSizing: 'border-box',
    zIndex: 10,
    userSelect: 'none',
    boxShadow: 'none',
  };

  const filterButtonActive: React.CSSProperties = {
    ...filterButtonBase,
    background: 'rgb(44, 45, 48)',
    color: '#ebebeb',
    borderRadius: '10px',
    fontWeight: '500',
    fontSize: '11px',
    flexGrow: 0,
    minWidth: '33%',
  };

  const filterButtonInactive: React.CSSProperties = {
    ...filterButtonBase,
    background: 'transparent',
    color: '#a3a3a3',
    fontWeight: '400',
    borderRadius: '10px',
    minWidth: '33%',
    fontSize: '12px',
  };

  // --- ICON STYLES FOR TOP TAB ---
  const iconWrapper: React.CSSProperties = {
    width: '29px',
    height: '29px',
    borderRadius: '50%',
    backgroundColor: '#202020',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
  };

  const topPlayerIcon: React.CSSProperties = {
    width: '32px',
    height: '32px',
  };

  // --- SHARED TEXT STYLE FOR TOP TAB ---
  // Ensure consistent typography for top list items
  const topBaseText: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: '500',
    fontVariantNumeric: 'lining-nums tabular-nums',
    letterSpacing: '0.2px',
    lineHeight: '13px', // Explicit line height for tighter, consistent spacing
    marginLeft: '3px',
    marginRight: '10px',
  };

  // --- NEW TOP ROW STYLES FOR CONSISTENT ALIGNMENT ---
  const topRow: React.CSSProperties = {
    ...topBaseText,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '8px 4px', // Increased padding for better visual separation
    marginTop: '4px',
    borderRadius: '18px',
    backgroundColor: '#121212', // Matches screenshot background color
    minHeight: '80px',
  };

  const topPlayerInfo: React.CSSProperties = {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 4px', // Adjusted to match overall inner padding
  };

  const topPlayerDetails: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    minWidth: '120px',
  };

  const topStatsContainer: React.CSSProperties = {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    flexWrap: 'nowrap', // Keep the two groups on one line
    marginTop: '8px',
    padding: '0 4px', // Adjusted to match overall inner padding
  };

  const topCurrencyGroup: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexGrow: 1, // Takes up half the space
    minWidth: '120px',
    paddingRight: '12px', // Space between the two main groups
    // Nested flexbox for labels/values
    gap: '2px', // Vertical spacing between lines
  };

  const topStatGroup: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flexGrow: 1, // Takes up the other half
    minWidth: '120px',
    gap: '2px', // Vertical spacing between lines
  };

  const topStatColumn: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
  };

  // --- UPDATED INDIVIDUAL TEXT STYLES ---
  const topPlayerName: React.CSSProperties = {
    ...topBaseText,
    color: '#bbbfc5',
    fontSize: '12px', // Slightly larger for name
    lineHeight: '1.2',
  };
  const topPlayerDate: React.CSSProperties = {
    ...topBaseText,
    color: '#7b7b7b',
    fontWeight: '400',
    fontSize: '11px',
    lineHeight: '1.2',
  };

  const topCurrencyLabel: React.CSSProperties = {
    ...topBaseText,
    color: '#7b7b7b',
    fontWeight: '400',
    lineHeight: '1.5',
    minHeight: '15px', // Consistent line height
  };
  const topCurrencyValue: React.CSSProperties = {
    ...topBaseText,
    color: '#bbbfc5',
    lineHeight: '1.5',
    minHeight: '15px',
    fontWeight: '400',
  };

  const topStatLabel: React.CSSProperties = {
    ...topBaseText,
    color: '#7b7b7b',
    lineHeight: '1.5',
    minHeight: '15px',
    fontWeight: '400',
  };

  const topStatValue: React.CSSProperties = {
    ...topBaseText,
    // Use an explicit color from the screenshot, darker purple-pink
    color: 'rgb(206, 36, 195)',
    letterSpacing: '0.3px',
    lineHeight: '1.5',
    minHeight: '15px',
    fontWeight: '400', // Matches the bold look
    // fontFamily: 'Inter,sans-serif',
  };
  // ----------------------------------------

  return {
    // --- LOADING SPINNER STYLES ---
    loaderContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%',
      minHeight: '100px',
    },
    circularLoader: {
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      borderTop: '3px solid #ff0000',
      animation: 'spin 1s linear infinite',
    },
    // ------------------------------

    leftPanelDesktop: {
      display: 'flex',
      flexDirection: 'column',
      height: '94%',
      marginTop: '43px',
      width: '100%',
      minWidth: '316px',
      background: '#1b1c1d',
      borderRadius: '20px',
      padding: '8px 8px 0',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      userSelect: 'none',
    },

    leftPanelMobile: {
      position: 'relative',
      bottom: 'clamp(5px, 1.5vh, 80px)',
      left: '-10px',
      right: '0px',
      height: 'clamp(400px, 60vh, 500px)',
      maxHeight: 'clamp(400px, 60vh, 500px)',
      background: 'rgba(25, 27, 30, 0.95)',
      borderRadius: 'clamp(12px, 1.9vw, 20px) clamp(12px, 1.9vw, 20px) 0 0',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      width: '100%',
      transform: 'translateY(calc(100% - clamp(500px, 20vh, 1000px)))',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 1500,
      overflow: 'hidden',
      touchAction: 'pan-y',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      userSelect: 'none',
    },
    leftPanelMobileExpanded: {
      position: 'fixed',
      bottom: '0px',
      left: '0px',
      right: '0px',
      height: 'calc(clamp(400px, 60vh, 500px) - clamp(58px, 6.9vh, 80px))',
      maxHeight: 'calc(clamp(400px, 60vh, 500px) - clamp(58px, 6.9vh, 80px))',
      background: 'rgba(25, 27, 30, 0.95)',
      borderRadius: 'clamp(12px, 1.9vw, 20px) clamp(12px, 1.9vw, 20px) 0 0',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      width: '100%',
      transform: 'translateY(0px)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 1500,
      overflow: 'hidden',
      touchAction: 'pan-y',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      userSelect: 'none',
    },
    leftPanelTablet: {
      position: 'fixed',
      bottom: 'clamp(100px, 28vh, 180px)',
      left: '0px',
      right: '0px',
      height: 'clamp(50px, 10vh, 80px)',
      background: 'rgba(25, 27, 30, 0.9)',
      borderRadius: 'clamp(12px, 1.9vw, 20px) clamp(12px, 1.9vw, 20px) 0 0',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      width: '100%',
      transform: 'translateY(calc(100% - clamp(60px, 7.1vh, 90px)))',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 1500,
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      userSelect: 'none',
    },
    leftPanelTabletExpanded: {
      position: 'fixed',
      bottom: '0px',
      left: '0px',
      right: '0px',
      height: 'calc(clamp(400px, 60vh, 500px) - clamp(58px, 6.9vh, 80px))',
      maxHeight: 'calc(clamp(400px, 60vh, 500px) - clamp(58px, 6.9vh, 80px))',
      background: 'rgba(25, 27, 30, 0.95)',
      borderRadius: 'clamp(12px, 1.9vw, 20px) clamp(12px, 1.9vw, 20px) 0 0',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      width: '100%',
      transform: 'translateY(0px)',
      transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: 1500,
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      userSelect: 'none',
    },
    leftPanelTabs: {
      display: 'flex',
      width: '100%',
      minHeight: '32px',
      height: 'auto',
      background: '#141516',
      borderRadius: '64px',
      border: 'solid 4px #141516',
      margin: '0 auto',
      maxWidth: '400px',
      pointerEvents: 'auto',
    },
    leftPanelTab: {
      flexGrow: '1',
      flexShrink: 1,
      minWidth: 0,
      height: '100%',
      width: '100%',
      textAlign: 'center',
      background: 'none',
      borderRadius: '32px',
      border: 'none',
      padding: '0 4px',
      color: '#a3a3a3',
      fontSize: '11px',
      fontWeight: '500',
      letterSpacing: '-.18px',
      lineHeight: 1.15,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color .25s ease-in, color .25s ease-in',
      cursor: 'pointer',
      pointerEvents: 'auto',
      boxShadow: 'none',
      marginBottom: '2px',
      boxSizing: 'border-box',
    },
    leftPanelTabActive: {
      background: '#2c2d30',
      color: '#ebebeb',
      cursor: 'default',
      flexGrow: '1',
      flexShrink: 1,
      minWidth: 0,
      height: '100%',
      width: '100%',
      textAlign: 'center',
      borderRadius: '32px',
      border: 'none',
      padding: '0 4px',
      fontSize: '11px',
      fontWeight: '300',
      letterSpacing: '-.18px',
      lineHeight: 1.15,
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      overflowWrap: 'anywhere',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      transition: 'background-color .25s ease-in, color .25s ease-in',
      pointerEvents: 'auto',
      boxShadow: 'none',
    },
    betList: {
      borderRadius: '16px',
      margin: '0',
      display: 'block',
      position: 'relative',
      willChange: 'scroll-position',
      transform: 'translateY(0px)',
      overflowY: 'auto',
      minWidth: '100%',
      WebkitOverflowScrolling: 'touch',
      scrollbarWidth: 'thin',
      scrollbarColor: 'rgba(255, 255, 255, 0.3) transparent',
      maxWidth: '100%',
      left: '0',
      contain: 'content',
      overflow: 'hidden',
      marginBottom: '0px',
    },
    betListHeader: {
      display: 'flex',
      padding: '4px',
      marginBottom: '4px',
      justifyContent: 'center',
      alignItems: 'center',
      color: '#7b7b7b',
      fontSize: '10px',
      fontWeight: '500',
      letterSpacing: '-.15px',
    },
    betListHeaderItem: {
      height: '12px',
      width: '100%',
      minWidth: '72px',
      marginLeft: '4px',
      padding: '0 4px',
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    betRow: {
      display: 'flex',
      alignItems: 'center',
      height: '32px',
      width: '100%',
      padding: '4px',
      marginTop: '4px',
      borderRadius: '18px',
      background: '#101112',
    },
    betRowWinner: {
      background:
        'linear-gradient(0deg, rgba(66, 127, 0, .3) 0%, rgba(66, 127, 0, .3) 100%, #101112)',
    },
    betRowCell: {
      width: '100%',
      fontSize: '12px',
      color: '#bbbfc5',
      fontWeight: '500',
      textAlign: 'right',
      marginLeft: '4px',
      padding: '0 4px',
      whiteSpace: 'nowrap',
    },
    username: {
      width: '100%',
      padding: '0 4px',
      letterSpacing: '-0.18px',
      textAlign: 'left',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
    },
    dataListContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: '0',
      marginTop: '4px',
    },
    roundSummaryContainer: {
      display: 'flex',
      width: '100%',
      minWidth: '300px',
      height: '68px',
      padding: '8px',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '14px',
      background: '#141516',
      marginBottom: '4px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
      width: ' 100%',
      height: '20px',
    },
    summaryBottomRow: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },
    avatarContainer: {
      display: 'flex',
      position: 'relative',
      alignItems: 'center',
    },
    avatarImage: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      border: 'solid 1px #28a90a',
      verticalAlign: 'middle',
    },
    betCountText: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
    },
    betCount: {
      fontWeight: '500',
      fontSize: '12px',
      lineHeight: '100%',
      verticalAlign: 'middle',
      color: '#ccc',
    },
    betLabel: {
      paddingLeft: '4px',
      fontSize: '12px',
      color: '#7b7b7b',
    },
    totalWinContainer: {
      textAlign: 'right' as const,
      fontSize: '12px',
      verticalAlign: 'middle' as const,
    },
    totalWinAmount: {
      color: '#ccc',
      fontWeight: '600',
      fontSize: '16px',
      lineHeight: '18px',
      verticalAlign: 'middle',
    },
    totalWinLabel: {
      fontWeight: '400',
      fontSize: '12px',
      lineHeight: '100%',
      verticalAlign: 'middle',
      color: '#7b7b7b',
    },
    stats: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
      height: '16px',
      width: '100%',
    },
    progress: {
      width: '100%',
      height: '8px',
      background: '#2b2b2b80',
      borderRadius: '32px',
      overflow: 'hidden',
      padding: '2px',
      lineHeight: '0',
      fontSize: '.75rem',
      display: 'flex',
    },
    roundProgressBar: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      overflow: 'hidden',
      color: '#fff',
      textAlign: 'center',
      whiteSpace: 'nowrap',
      height: '100%',
      background: '#427f00',
      transition: 'width .3s ease',
      borderRadius: '32px',
    },

    betListContainer: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      paddingBottom:
        deviceType === 'mobile'
          ? FOOTER_HEIGHT
          : `calc(${FOOTER_HEIGHT} + 10px)`,
    },

    noBetsNotice: {
      textAlign: 'center',
      color: '#A0AEBB',
      fontSize: 'clamp(11px, 1.5vw, 15px)',
      paddingTop: 'clamp(16px, 2.4vh, 24px)',
    },
    dragHandle: {
      width: 'clamp(32px, 4.6vw, 48px)',
      height: 'clamp(3px, 0.5vh, 5px)',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      borderRadius: 'clamp(1px, 0.2vw, 3px)',
      margin: 'clamp(6px, 1vw, 12px) auto',
      cursor: 'grab',
      flexShrink: 0,
    },
    expandCollapseButton: {
      width: '44px',
      height: '44px',
      borderRadius: '12px',
      backgroundColor: '#2c2d30',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '4px',
      cursor: 'pointer',
      position: 'absolute',
      top: '-52px',
      right: '12px',
      zIndex: 1501,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
    },

    // --- MODAL STYLES ---
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      paddingTop: '28px',
      zIndex: 9999,
    },
    modalContent: {
      borderRadius: '10px',
      width: '100%',
      maxWidth: '1150px',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.5)',
      display: 'flex',
      flexDirection: 'column',
      color: '#e8f0f8',
      backgroundColor: '#2D3C27',
      padding: '0',
      overflow: 'hidden',
      maxHeight: deviceType === 'mobile' ? '84vh' : '90vh',
      minHeight: '290px',
      overflowY: 'auto',
      marginRight: '5px',
    },
    modalHeaderContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px 20px',
      backgroundColor: '#2c2d30',
      flexShrink: 0,
      height: '3px',
    },
    modalHeaderScrollActive: {
      boxShadow: '0 4px 6px -2px rgba(0,0,0,0.18)',
    },
    modalBody: {
      backgroundColor: '#2D3C27',
      padding: '10px 18px',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    modalCloseButton: {
      background: 'none',
      border: 'none',
      color: '#97a4ae',
      fontSize: '24px',
      fontWeight: 'bold',
      fontStretch: ' expanded',
      cursor: 'pointer',
      padding: '0 10px',
      minWidth: '40px',
      lineHeight: 1,
      marginRight: '-20px',
      boxShadow: 'none',
    },
    modalCloseButtonHover: {
      color: '#fff',
      transition: 'all 0.15s ease-in-out', // Smooth transition
    },
    modalHeader: {
      fontSize: '17px',
      fontWeight: '400',
      color: '#fff',
      margin: 0,
      textAlign: 'center' as const,
      textTransform: 'uppercase' as const,
    },
    modalText: {
      fontSize: '14px',
      lineHeight: 1.5,
      color: '#9ea0a3',
      margin: '5px 0',
      textAlign: 'left' as const,
    },
    highlightedParagraph: {
      backgroundColor: '#21291B',
      padding: '10px',
      borderRadius: '8px',
      margin: '10px',
      color: '#9ea0a3',
      fontSize: '14px',
      lineHeight: 1.6,
      textAlign: 'left' as const,
    },
    topSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center' as const,
      padding: '10px 0',
    },
    diagramSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '20px 0',
      backgroundColor: '#2D3C27',
      margin: '0 -20px',
      paddingLeft: '20px',
      paddingRight: '20px',
    },
    diagramParticipantIcon: {
      width: '40px',
      height: '40px',
      marginBottom: '5px',
      filter:
        'invert(80%) sepia(30%) saturate(600%) hue-rotate(80deg) brightness(110%) contrast(90%)',
    },
    participantRow: {
      display: 'flex',
      justifyContent: 'space-around',
      width: '100%',
      marginBottom: '10px',
      maxWidth: '450px',
    },
    participantBox: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    seedLabel: {
      backgroundColor: '#333',
      color: '#fff',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      marginBottom: '5px',
      minWidth: '30px',
      textAlign: 'center' as const,
      fontWeight: 'bold',
    },
    plusSign: {
      fontSize: '20px',
      fontWeight: 'bold',
      color: '#fff',
      position: 'relative' as const,
      top: '-10px',
    },
    downArrow: {
      color: '#fff',
      fontSize: '20px',
      margin: '5px 0',
      fontWeight: 'bold',
    },
    downArrowLarge: {
      color: '#fff',
      fontSize: '30px',
      margin: '10px 0',
      fontWeight: 'bold',
      lineHeight: '0.5',
    },
    combinedBox: {
      backgroundColor: '#333',
      color: '#fff',
      padding: '8px 12px',
      borderRadius: '4px',
      fontSize: '14px',
      fontWeight: '600',
      textAlign: 'center' as const,
      margin: '10px 0',
      letterSpacing: '2px',
    },
    sha512Box: {
      backgroundColor: '#2c2d30',
      color: '#fff',
      padding: '15px',
      borderRadius: '4px',
      textAlign: 'center' as const,
      wordBreak: 'break-all' as const,
      margin: '10px 0',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      maxWidth: '85%',
    },
    hashLabel: {
      fontSize: '12px',
      color: '#fff',
      marginBottom: '5px',
      fontWeight: 'bold',
      textTransform: 'uppercase' as const,
    },
    hashValue: {
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: 1.4,
      color: '#A0AEBB',
    },
    resultMultiplierBox: {
      backgroundColor: '#2c2d30',
      color: '#7cfc00',
      padding: '10px 15px',
      borderRadius: '4px',
      fontSize: '24px',
      fontWeight: 'bold',
      textAlign: 'center' as const,
      margin: '10px 0',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    sectionTitle: {
      textAlign: 'center' as const,
      fontSize: '14px',
      color: '#fff',
      fontWeight: '400',
      margin: '0px 0px 5px 0px',
      textTransform: 'uppercase' as const,
    },
    subTitle: {
      fontSize: '14px',
      color: '#fff',
      fontWeight: '400',
      margin: '10px 0',
    },
    howToCheckList: {
      listStyleType: 'none',
      paddingLeft: '0',
      color: '#A0AEBB',
      fontSize: '14px',
      lineHeight: 1.6,
      margin: '10px 0',
    },

    scoreBoardFooter: {
      height: FOOTER_HEIGHT,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      background: '#6F6F6F5C',
      borderBottomLeftRadius: '16px',
      borderBottomRightRadius: '16px',
      flexShrink: 0,
      padding: '0',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10,
      overflow: 'hidden',
      pointerEvents: 'auto',
    },

    provablyFairContainer: {
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '12px',
      cursor: 'pointer',
      outline: 'none',
      boxShadow: 'none',
    },

    provablyFairIcon: {
      width: '13px',
      height: '13px',
      marginRight: '6px',
      flexShrink: 0,
    },
    provablyFairText: {
      color: '#A0AEBB',
      fontSize: '10px',
      textDecoration: 'none',
      whiteSpace: 'nowrap',
      transition: 'color 0.2s ease',
    },
    poweredByPlayAIContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingRight: '12px',
    },
    scoreBoardFooterText: {
      color: '#A0AEBB',
      fontSize: '10px',
      marginRight: '8px',
      opacity: '0.4',
    },
    scoreBoardFooterLink: {
      color: '#b0b8b0ff',
      fontSize: 'clamp(10px, 1.3vw, 12px)',
      fontWeight: '600',
      cursor: 'pointer',
    },
    previousResultContainer: {
      display: 'flex',
      width: '100%',
      minWidth: '300px',
      height: '68px',
      padding: '8px',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '14px',
      background: '#141516',
      marginBottom: '4px',
    },
    previousResultLabel: {
      fontSize: '12px',
      color: '#bbbfc5',
      fontWeight: '400',
      letterSpacing: '-.18px',
      margin: '0',
      marginBottom: '2px',
      fontStyle: 'lining-nums tabular-nums',
    },
    previousResultX: {
      fontSize: '24px',
      fontWeight: '700',
      margin: '4px 0 0 0',
      lineHeight: 'normal',
      marginTop: '0',
      fontStyle: 'bold',
    },
    topFiltersContainer: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '70px',
      padding: '2px',
      justifyContent: 'space-around',
      alignItems: 'center',
      borderRadius: '14px',
      background: '#141516',
      marginBottom: '0px',
      gap: '1px',
    },
    topFilterButtonGroup: {
      display: 'flex',
      justifyContent: 'space-between',
      textAlign: 'center',
      width: '100%',
      height: '28px',
      gap: '2px',
      padding: '0 4px',
    },
    topFilterButton: filterButtonInactive,
    topFilterButtonActive: filterButtonActive,
    filterTabsContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      padding: '2px 0',
      background: theme.colors.background2 || '#0d0d0d',
      cursor: 'pointer',
      pointerEvents: 'auto',
    },
    filterTab: {
      flex: '0 0 auto',
      minWidth: '50px',
      textAlign: 'center',
      color: theme.colors.background4 || '#A0AEBB',
      background: theme.colors.background3 || 'transparent',
      borderRadius: '6px',
      cursor: 'pointer',
      padding: '2px 8px',
      transition: 'all 0.2s ease',
    },
    filterTabActive: {
      flex: '1',
      minWidth: '50px',
      textAlign: 'center',
      color: theme.colors.text || '#fff',
      borderRadius: '6px',
      cursor: 'pointer',
      padding: '4px 8px',
      transition: 'all 0.2s ease',
    },
    leftPanelTabTop: {
      flexGrow: '20',
      height: '100%',
      width: '100%',
      textAlign: 'center',
      background: 'none',
      borderRadius: '32px',
      border: 'none',
      padding: '0',
      color: '#a3a3a3',
      fontSize: '12px',
      fontWeight: '400',
      letterSpacing: '-.18px',
      transition: 'background-color .25s ease-in, color .25s ease-in',
      cursor: 'pointer',
      pointerEvents: 'auto',
      boxShadow: 'none',
    },
    leftPanelTabsTop: {
      display: 'flex',
      width: '100%',
      height: '32px',
      borderRadius: '64px',
      margin: '0 auto',
      maxWidth: '400px',
      pointerEvents: 'auto',
    },
    iconWrapper: iconWrapper,
    topPlayerIcon: topPlayerIcon,

    // --- ROUNDS ROW STYLES (UPDATED FOR CONSISTENCY) ---
    roundsRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      height: '30px',
      width: '100%',
      padding: '2px 6px',
      marginTop: '2px',
      borderRadius: '18px',
      backgroundColor: '#101112',
    },
    roundsDateCell: {
      ...topBaseText, // Apply shared style
      color: '#bbbfc5',
      flex: 3,
      textAlign: 'left',
    },
    roundsRightContainer: {
      flex: 2,
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
    },
    roundsMultiplier: {
      ...topBaseText, // Apply shared style
      width: '80px',
      textAlign: 'center',
      flexShrink: 0,
    },
    roundsShieldContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: '#242526',
      border: '1px solid #363738',
    },

    // --- NEW/UPDATED STYLES FOR TOP X/WIN CARDS ---
    topRow: topRow,
    topPlayerInfo: topPlayerInfo,
    topPlayerDetails: topPlayerDetails,
    topStatsContainer: topStatsContainer,
    topCurrencyGroup: topCurrencyGroup,
    topStatGroup: topStatGroup,
    topLeftAlignCol: {
      ...topStatColumn,
      alignItems: 'flex-start',
      width: '45%', // Take less space to give stat values more room
      minWidth: '50px',
    },
    topRightAlignCol: {
      ...topStatColumn,
      alignItems: 'flex-end',
      width: '55%', // Take more space for values
      minWidth: '70px',
    },

    // Apply the refined text styles
    topPlayerName: topPlayerName,
    topPlayerDate: topPlayerDate,
    topStatLabel: topStatLabel,
    topStatValue: topStatValue,
    topCurrencyLabel: topCurrencyLabel,
    topCurrencyValue: topCurrencyValue,
  };
};
